import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, FlaskConical, Loader2, Play, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useConfirm } from "@/components/ConfirmDialog";
import { NewTestDialog } from "@/components/NewTestDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { NextRunsList } from "@/components/CountdownCell";
import { RefBadge } from "@/components/RefBadge";

export default function SuiteDetailPage() {
  const { projectId, suiteId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [suite, setSuite] = useState(null);
  const [tests, setTests] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [runningId, setRunningId] = useState(null);
  const [runningSuite, setRunningSuite] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [schedulesByEntity, setSchedulesByEntity] = useState({});

  const load = useCallback(() => {
    Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/suites/${suiteId}`),
      api.get(`/suites/${suiteId}/tests`),
      api.get(`/projects/${projectId}/environments`),
      api.get(`/schedules?status=active&project_id=${projectId}`),
    ])
      .then(([p, s, t, e, sch]) => {
        setProject(p.data);
        setSuite(s.data);
        setTests(t.data.items || []);
        setEnvironments(e.data.items || []);
        const grouped = {};
        for (const item of sch.data.items || []) {
          if (!grouped[item.entity_id]) grouped[item.entity_id] = [];
          grouped[item.entity_id].push(item);
        }
        setSchedulesByEntity(grouped);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId, suiteId]);

  useEffect(() => { load(); }, [load]);

  const envById = (id) => environments.find((e) => e.id === id);
  const suiteDefaultEnv = envById(suite?.default_environment_id);

  const handleRunSuite = async (environmentId = null) => {
    setRunningSuite(true);
    try {
      const qs = environmentId ? `?environment_id=${environmentId}` : "";
      const { data } = await api.post(`/suites/${suiteId}/run${qs}`);
      navigate(`/suite-runs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunningSuite(false);
    }
  };

  const handleRunTest = async (t) => {
    setRunningId(t.id);
    try {
      const { data: run } = await api.post(`/tests/${t.id}/run`);
      navigate(`/runs/${run.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunningId(null);
    }
  };

  const handleDelete = async (t) => {
    const ok = await confirm({
      title: "Delete this test?",
      description: `"${t.name}" will be permanently removed.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try { await api.delete(`/tests/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    {
      key: "name",
      label: "Test",
      mobileVariant: "title",
      render: (t) => (
        <div className="flex flex-col">
          <RefBadge value={t.ref} testId={`suite-test-ref-${t.id}`} />
          <Link to={`/tests/${t.id}`} className="rt-primary hover:text-[#3B82F6] mt-1" data-testid={`suite-test-link-${t.id}`}>
            {t.name}
          </Link>
        </div>
      ),
    },
    {
      key: "target",
      label: "Target URL",
      render: (t) => (
        <span className="rt-value text-xs text-[#64748B] inline-flex items-center gap-1 truncate font-mono">
          {t.target_url.replace(/^https?:\/\//, "").slice(0, 40)}
          <ExternalLink size={10} />
        </span>
      ),
    },
    { key: "steps", label: "Steps", render: (t) => <span className="text-[#334155]">{t.steps_count}</span> },
    {
      key: "last_run",
      label: "Last run",
      render: (t) => {
        const upcoming = schedulesByEntity[t.id] || [];
        return (
          <div className="flex flex-col gap-0">
            {t.last_run ? (
              <Link to={`/runs/${t.last_run.id}`} className="inline-flex flex-col gap-0.5">
                <StatusBadge status={t.last_run.status} />
                <span className="text-[11px] text-[#94A3B8]">
                  {t.last_run.started_at ? formatDateTime(t.last_run.started_at) : ""}
                </span>
              </Link>
            ) : (
              <StatusBadge status={null} />
            )}
            <NextRunsList schedules={upcoming} testIdPrefix={`suite-test-next-${t.id}`} />
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (t) => (
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => handleRunTest(t)} disabled={runningId === t.id} data-testid={`run-suite-test-${t.id}`} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50">
            {runningId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Run
          </button>
          <button onClick={() => handleDelete(t)} className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2]" aria-label="Delete">
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  if (!suite) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 text-sm text-[#64748B] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading suite…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="suite-detail-page">
      <div className="flex items-center gap-1.5 text-xs text-[#64748B] mb-3">
        <button onClick={() => navigate("/projects")} className="hover:text-[#3B82F6]">Projects</button>
        <ChevronRight size={12} />
        <button onClick={() => navigate(`/projects/${projectId}/suites`)} className="hover:text-[#3B82F6]">Suites</button>
        <ChevronRight size={12} />
        <span className="text-[#0F172A] font-medium truncate">{suite.name}</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <RefBadge value={suite.ref} testId="suite-detail-ref" />
          <h1 className="font-['Geist'] font-semibold text-2xl sm:text-[28px] tracking-tight text-[#0F172A] leading-tight mt-2">
            {suite.name}
          </h1>
          {suite.description && <p className="mt-1.5 text-sm text-[#64748B] max-w-2xl">{suite.description}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {suiteDefaultEnv && (
              <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-[#F8FAFC] border border-[#E5E7EB] text-[#334155]" data-testid="suite-default-env-badge">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: suiteDefaultEnv.type_color || "#64748B" }} />
                <span className="font-medium">{suiteDefaultEnv.name}</span>
                <span className="text-[9px] uppercase tracking-wide text-[#94A3B8]">{suiteDefaultEnv.type}</span>
              </span>
            )}
            {suite.tags?.map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC]" data-testid="suite-new-test-button">
            <Plus size={14} />
            New test
          </button>
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => handleRunSuite()}
              disabled={runningSuite || !tests?.length}
              data-testid="suite-run-all-button"
              className={`inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 ${environments.length > 1 ? "rounded-l-md" : "rounded-md"} text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50`}
            >
              {runningSuite ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {suiteDefaultEnv ? `Run on ${suiteDefaultEnv.name}` : "Run suite"} ({tests?.length || 0})
            </button>
            {environments.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="suite-run-env-trigger"
                    disabled={runningSuite || !tests?.length}
                    className="inline-flex items-center justify-center w-9 h-10 rounded-r-md bg-[#3B82F6] text-white border-l border-[#2563EB] hover:bg-[#2563EB] disabled:opacity-50"
                    aria-label="Pick an environment to run on"
                  >
                    <ChevronDown size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                  <DropdownMenuLabel className="text-[11px] text-[#94A3B8] uppercase tracking-wide">Run this suite on…</DropdownMenuLabel>
                  {environments.map((e) => (
                    <DropdownMenuItem
                      key={e.id}
                      onClick={() => handleRunSuite(e.id)}
                      data-testid={`suite-run-on-env-${e.id}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: e.type_color || "#64748B" }} />
                      <span className="flex-1">{e.name}</span>
                      <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] ml-2">{e.type}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {tests === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : tests.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-suite-tests">
          <FlaskConical size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No tests in this suite</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">Add a test directly to this suite — or move existing tests from another suite via the Tests tab.</p>
        </div>
      ) : (
        <ResponsiveTable
          testId="suite-tests-table"
          columns={columns}
          rows={tests}
          getRowTestId={(t) => `suite-test-row-${t.id}`}
        />
      )}

      <NewTestDialog
        key={`new-suite-test-${suiteId}-${suite?.default_environment_id || ""}`}
        open={newOpen}
        onOpenChange={setNewOpen}
        projectId={projectId}
        suites={[suite]}
        environments={environments}
        defaultSuiteId={suiteId}
        defaultEnvironmentId={suite?.default_environment_id || environments[0]?.id}
        defaultTargetUrl={project?.default_target_url || ""}
      />
    </div>
  );
}
